/**
 * NativeBootstrap — mounts once when running inside the Capacitor shell
 * (iOS / Android). Does nothing on the web (no-op render).
 *
 * Responsibilities:
 *   1. Register the device with APNs / FCM and POST the token to the backend
 *      (`/api/push/register`) so admins can broadcast Fêtes 2027 reminders.
 *   2. Hook the Android hardware "back" button to React Router navigation.
 *   3. Handle deep-links (e.g. `presence://checkin/xxx`) by forwarding them
 *      into `history.pushState` — feature reserved for a later iteration.
 *
 * Kept intentionally minimal — real UX (permission modals, opt-out settings)
 * lives on the `/espace/notifications` screen.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { isNative, platform, registerPushNotifications } from '@/lib/native';

const API = process.env.REACT_APP_BACKEND_URL;

async function postTokenToBackend(token) {
  try {
    await axios.post(`${API}/api/push/register`, {
      token,
      platform: platform(), // 'ios' | 'android'
      locale:
        (typeof localStorage !== 'undefined' &&
          localStorage.getItem('prieure_locale')) ||
        'fr',
      app_version: '1.0.0',
    });
  } catch {
    // Silent — the app remains fully usable without push.
  }
}

export default function NativeBootstrap() {
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (!isNative() || started.current) return;
    started.current = true;

    let cleanupBack = () => {};
    (async () => {
      const { App } = await import('@capacitor/app');
      // Android hardware back → SPA history
      const sub = await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) window.history.back();
        else App.exitApp();
      });
      cleanupBack = () => sub.remove();

      // Push registration (permission prompt happens here).
      registerPushNotifications({
        onRegister: postTokenToBackend,
        onNotification: (notif) => {
          const path = notif?.notification?.data?.path;
          if (path && typeof path === 'string' && path.startsWith('/')) {
            navigate(path);
          }
        },
      });
    })();

    return () => {
      cleanupBack();
    };
  }, [navigate]);

  return null;
}
